<script setup lang="ts">
import { computed, ref, useSlots, type HTMLAttributes } from "vue";
import { cn } from "@/lib/utils";

/**
 * A dense editor field (design skill §6): uppercase 11px label → control →
 * optional hint/error. Put the control (compact Input/Select/Switch) in the
 * default slot. Dense by design — for editor sidebars, not roomy pages.
 *
 * `info` (or the `#info` slot for richer content) renders a click- and
 * keyboard-accessible popover beside the label with a concise plain-English
 * explanation of the setting. Keep it short — inline validation stays in
 * `error`, not here.
 */
const props = defineProps<{
  label?: string;
  hint?: string;
  error?: string;
  info?: string;
  class?: HTMLAttributes["class"];
}>();

const infoOpen = ref(false);
const slots = useSlots();
const hasInfo = computed(() => Boolean(props.info) || Boolean(slots.info));
</script>

<template>
  <div data-slot="editor-field" :class="cn('space-y-1.5', props.class)">
    <div v-if="props.label || hasInfo" class="flex items-center gap-1">
      <label
        v-if="props.label"
        class="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
      >
        {{ props.label }}
      </label>
      <Popover v-if="hasInfo" v-model:open="infoOpen">
        <PopoverTrigger as-child>
          <button
            type="button"
            class="grid size-3.5 shrink-0 place-items-center rounded-full text-muted-foreground/70 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            :aria-label="props.label ? `More about ${props.label}` : 'More information'"
          >
            <Icon mode="svg" size="11" name="lucide:info" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="top"
          align="start"
          class="w-64 p-2.5 text-[12px] leading-relaxed text-popover-foreground"
        >
          <slot name="info">{{ props.info }}</slot>
        </PopoverContent>
      </Popover>
    </div>
    <slot />
    <p v-if="props.error" class="text-[11.5px] text-destructive">{{ props.error }}</p>
    <p v-else-if="props.hint" class="text-[11.5px] text-muted-foreground">{{ props.hint }}</p>
  </div>
</template>
